// ============================================================================
// LES VALEURS OFFERTES AUX CHAMPS À CHOIX — et ce qu'on y ajoute après une création.
//
// ⚠️ POURQUOI CETTE FONCTION VIT HORS DE L'ÉCRAN (correctif d'audit 2026-08-25). La fiche RELISAIT
// tout le catalogue après chaque création de référentiel — un balayage à sept blocs mesuré entre 761
// et 965 ms — au motif qu'« une valeur créée restait absente de la liste ». Pour famille, secteur,
// fournisseur, marque et saison, c'était FAUX et ça le restera : ces dimensions sont DÉRIVÉES des
// colonnes texte des articles (`ARTICLE_FACETS`, serveur : « l'ensemble des valeurs EST le catalogue,
// et une valeur inutilisée n'existe pas »). Une famille que zéro article ne porte n'y apparaît jamais.
//
// ⚠️ POUR SOUS-FAMILLE ET COLLECTION, LA RELECTURE MARCHAIT — ce sont des `REFERENTIEL_FACETS`, servies
// par jointure à gauche sur leur table, précisément pour qu'« une collection créée à l'instant soit
// choisissable ». On ne leur retire donc pas une capacité : on la leur rend SANS le balayage, comme
// aux cinq autres.
//
// ⚠️ CE N'EST PAS UNE DONNÉE INVENTÉE (§23.0) : le serveur a CONFIRMÉ la création. La valeur ajoutée est
// celle que le client lui a soumise (élaguée), car la route ne rend aucun libellé — si elle en rendait
// un un jour, c'est LUI qu'il faudrait poser, la normalisation appartenant au référentiel.
// ============================================================================
import { useCallback, useMemo, useState } from 'react';
import type { ArticleFacet, ArticleFacets, FacetValue } from '../api';
import { createFacetValue } from './referentiel-facette';

/** Le compte d'une valeur qu'aucun article ne porte encore — 0, et pas une absence. */
const NO_ARTICLE = 0;

/**
 * Ajoute une valeur fraîchement créée au jeu offert.
 *
 * ⚠️ ELLE VA EN QUEUE, ET C'EST L'ORDRE DU SERVEUR (revue 2026-08-25). Les facettes sont servies
 * `ORDER BY n DESC, valeur ASC` — le nombre d'articles d'abord, l'alphabet seulement en départage. Une
 * valeur à zéro article est donc la DERNIÈRE, et un tri alphabétique global l'aurait glissée au milieu
 * en réordonnant tout le reste : la liste que l'opérateur venait de parcourir aurait changé sous ses
 * yeux, ce qui est plus déroutant que la ligne qu'on cherchait à lui montrer.
 * ⚠️ IDEMPOTENTE : recréer une valeur déjà offerte ne la double pas — un doublon dans un choix est une
 * option qui ne veut rien dire.
 * ⚠️ RIEN SANS JEU CHARGÉ : si les offres ont échoué à se lire, les pickers sont inertes et le DISENT.
 * Y injecter une valeur seule ferait croire qu'ils fonctionnent.
 */
export function insererValeurOfferte(
  jeu: ArticleFacets | null,
  dim: ArticleFacet,
  valeur: string,
): ArticleFacets | null {
  const v = valeur.trim();
  if (jeu === null || v === '') return jeu;
  const actuelles = jeu.values[dim] ?? [];
  if (actuelles.some((x) => x.value === v)) return jeu;
  const suivantes: FacetValue[] = [...actuelles, { value: v, count: NO_ARTICLE }];
  return { ...jeu, values: { ...jeu.values, [dim]: suivantes } };
}

/**
 * Les valeurs offertes par un écran de CRÉATION d'article : celles de l'hôte, plus celles créées ici.
 *
 * ⚠️ CRÉER ET OFFRIR SONT LE MÊME GESTE (la fiche le dit déjà) : une valeur créée qui ne rejoint pas la liste
 *    laisse le sélecteur sur « À choisir », la valeur introuvable dans sa propre liste. « Article simple » et
 *    l'assistant Gamme faisaient la création seule — d'où ce geste unique, que les deux appellent.
 * ⚠️ UNE SURCOUCHE, PAS LE JEU DE L'HÔTE : la liste des articles tient ses facettes du serveur pour ses
 *    filtres, et une famille que zéro article ne porte n'y a pas sa place. Et la surcouche survit à une
 *    relecture de l'hôte : la valeur créée reste offerte tant que l'écran vit.
 * ⚠️ ON OFFRE CE QUE LE SERVEUR A RETENU, jamais la frappe : le référentiel normalise.
 */
export function useOfferedFacets(fromHost: ArticleFacets | null): {
  offered: ArticleFacets | null;
  createAndOffer: (facet: ArticleFacet, value: string) => Promise<string>;
} {
  const [created, setCreated] = useState<{ facet: ArticleFacet; value: string }[]>([]);
  const offered = useMemo(
    () => created.reduce<ArticleFacets | null>((jeu, c) => insererValeurOfferte(jeu, c.facet, c.value), fromHost),
    [fromHost, created],
  );
  const createAndOffer = useCallback(async (facet: ArticleFacet, value: string) => {
    const kept = await createFacetValue(facet, value);
    setCreated((list) => [...list, { facet, value: kept }]);
    return kept;
  }, []);
  return { offered, createAndOffer };
}
